import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Plus, Trash2, Copy, ShieldCheck, UserPlus, Check, ChevronDown, ChevronRight, LayoutDashboard, CheckSquare, Bot, Plug, Shield, Wallet, TrendingUp, FileText, Users as UsersIcon, Building2, Clock, UsersRound } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

// Sections that permissions can grant access to
const SECTIONS = [
  { key: "dashboard",   label: "Dashboard",   icon: LayoutDashboard },
  { key: "tasks",       label: "Product",     icon: CheckSquare },
  { key: "agent",       label: "Agent",       icon: Bot },
  { key: "apis",        label: "APIs",        icon: Plug },
  { key: "permissions", label: "Permissions", icon: Shield },
  { key: "cost",        label: "Cost",        icon: Wallet },
  { key: "adoption",    label: "Adoption",    icon: TrendingUp },
  { key: "content",     label: "Content",     icon: FileText },
  { key: "users",       label: "Users",       icon: UsersIcon },
  { key: "accounts",    label: "Accounts",    icon: Building2 },
  { key: "user-section", label: "User Section", icon: UsersRound },
];

const TEAM_PALETTE = [
  { bg: "#FFE4E6", text: "#BE123C" },
  { bg: "#E0F2FE", text: "#075985" },
  { bg: "#F3E8FF", text: "#6B21A8" },
  { bg: "#FEF3C7", text: "#92400E" },
  { bg: "#D1FAE5", text: "#065F46" },
  { bg: "#FFEDD5", text: "#9A3412" },
];

const teamTone = (idx) => TEAM_PALETTE[idx % TEAM_PALETTE.length];

function initials(name) {
  return (name || "?")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function AvatarPill({ name, tone }) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <div
        className="h-8 w-8 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0"
        style={{ background: tone.bg, color: tone.text }}
      >
        {initials(name)}
      </div>
    </div>
  );
}

function PermissionsDialog({ open, onOpenChange, member, agentNames, onSave }) {
  const [selected, setSelected] = useState([]);
  const [sectionPerms, setSectionPerms] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && member) {
      setSelected(member.permissions || []);
      setSectionPerms(member.section_permissions || {});
    }
  }, [open, member]);

  const toggle = (key) => {
    setSelected((prev) => (prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]));
  };

  const toggleSubTab = (modKey, tab) => {
    setSectionPerms((prev) => {
      const current = prev[modKey] || [];
      const next = current.includes(tab) ? current.filter((t) => t !== tab) : [...current, tab];
      return { ...prev, [modKey]: next };
    });
  };

  const setAllSubTabs = (modKey) => {
    setSectionPerms((prev) => ({ ...prev, [modKey]: [] })); // empty = all access
  };

  const save = async () => {
    setSaving(true);
    try {
      // Only persist sub-tab restrictions for modules that are granted
      const cleaned = {};
      for (const k of selected) {
        if (sectionPerms[k] && sectionPerms[k].length > 0) cleaned[k] = sectionPerms[k];
      }
      await onSave(member.id, selected, cleaned);
      onOpenChange(false);
      toast.success("Permissions updated");
    } catch {
      toast.error("Failed to update permissions");
    } finally {
      setSaving(false);
    }
  };

  if (!member) return null;
  // Modules that have sub-tabs (driven by Agents catalog or hardcoded)
  const SUB_TABBED = {
    tasks: ["Tasks", "Agent Workflow"],
    agent: agentNames,
    permissions: agentNames,
    cost: agentNames,
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="permissions-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Manage permissions</DialogTitle>
          <DialogDescription>
            Choose which sections <span className="font-medium text-slate-800">{member.name}</span> can access. Modules with sub-tabs let you grant access to specific tabs only.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2 py-2">
          {SECTIONS.map(({ key, label, icon: Icon }) => {
            const active = selected.includes(key);
            return (
              <button
                key={key}
                type="button"
                onClick={() => toggle(key)}
                data-testid={`perm-toggle-${key}`}
                className={`flex items-center gap-3 h-11 px-3 rounded-xl border text-sm transition-all ${
                  active ? "btn-brand border-transparent" : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
                }`}
              >
                <Icon className={`h-[18px] w-[18px] ${active ? "text-white" : "text-slate-500"}`} />
                <span className="flex-1 text-left font-medium">{label}</span>
                <span
                  aria-hidden="true"
                  className={`h-4 w-4 rounded-[4px] flex items-center justify-center transition-colors ${
                    active ? "bg-white text-slate-900" : "border border-slate-300 bg-white"
                  }`}
                >
                  {active && <Check className="h-3 w-3" strokeWidth={3} />}
                </span>
              </button>
            );
          })}
        </div>

        {/* Sub-tab restrictions for granted modules */}
        {selected.filter((k) => SUB_TABBED[k]?.length > 0).length > 0 && (
          <div className="space-y-3 pt-2 border-t border-slate-100">
            <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">Sub-tab access (optional)</p>
            {selected.filter((k) => SUB_TABBED[k]?.length > 0).map((modKey) => {
              const tabs = SUB_TABBED[modKey] || [];
              const allowed = sectionPerms[modKey] || [];
              const allAccess = allowed.length === 0;
              const modLabel = SECTIONS.find((s) => s.key === modKey)?.label || modKey;
              return (
                <div key={modKey} className="rounded-xl border border-slate-200 p-3" data-testid={`subperm-${modKey}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-800">{modLabel}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-md ${allAccess ? "bg-mint-100 text-emerald-700" : "bg-sky-100 text-sky-700"}`}>
                        {allAccess ? "All tabs" : `${allowed.length}/${tabs.length} tabs`}
                      </span>
                    </div>
                    {!allAccess && (
                      <button
                        type="button"
                        onClick={() => setAllSubTabs(modKey)}
                        className="text-xs font-medium text-blue-600 hover:underline"
                        data-testid={`subperm-allow-all-${modKey}`}
                      >
                        Grant all
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {tabs.map((t) => {
                      const checked = allAccess || allowed.includes(t);
                      return (
                        <button
                          key={t}
                          type="button"
                          onClick={() => toggleSubTab(modKey, t)}
                          data-testid={`subperm-${modKey}-${t}`}
                          className={`h-7 px-2.5 rounded-md text-xs font-medium border transition-all ${
                            checked
                              ? "bg-blue-600 text-white border-transparent"
                              : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                          }`}
                        >
                          {t}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="permissions-save">
            {saving ? "Saving…" : "Save permissions"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewMemberDialog({ open, onOpenChange, teams, defaultTeamId, onCreate }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [teamId, setTeamId] = useState(defaultTeamId || "");
  const [designation, setDesignation] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
      setEmail("");
      setTeamId(defaultTeamId || teams[0]?.id || "");
      setDesignation("");
    }
  }, [open, defaultTeamId, teams]);

  const submit = async () => {
    if (!name.trim() || !email.trim() || !teamId) {
      toast.error("Name, email and team are required");
      return;
    }
    setSubmitting(true);
    try {
      await onCreate({ name, email, team_id: teamId, designation, permissions: ["dashboard"] });
      onOpenChange(false);
      toast.success("Member added");
    } catch {
      toast.error("Failed to add member");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="new-member-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Add team member</DialogTitle>
          <DialogDescription>Invite someone to your workspace.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className="mt-1" data-testid="new-member-name" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" className="mt-1" data-testid="new-member-email" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Team</Label>
            <Select value={teamId} onValueChange={setTeamId}>
              <SelectTrigger className="mt-1" data-testid="new-member-team"><SelectValue placeholder="Select team" /></SelectTrigger>
              <SelectContent>
                {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Designation</Label>
            <Input value={designation} onChange={(e) => setDesignation(e.target.value)} placeholder="e.g. Product Manager" className="mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="new-member-submit">{submitting ? "Adding…" : "Add member"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TeamsManagerDialog({ open, onOpenChange, teams, membersByTeam, onCreate, onUpdate, onDelete, teamTonesById }) {
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [drafts, setDrafts] = useState({});

  useEffect(() => {
    if (open) {
      setNewName("");
      setDrafts({});
    }
  }, [open]);

  const rename = async (team) => {
    const next = (drafts[team.id] ?? team.name).trim();
    if (!next || next === team.name) {
      setDrafts((d) => ({ ...d, [team.id]: team.name }));
      return;
    }
    try {
      await onUpdate(team.id, { name: next });
      toast.success("Team renamed");
    } catch {
      toast.error("Rename failed");
    }
  };

  const addTeam = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      await onCreate({ name: newName.trim() });
      setNewName("");
      toast.success("Team created");
    } catch {
      toast.error("Create failed");
    } finally {
      setAdding(false);
    }
  };

  const removeTeam = async (team) => {
    if (!window.confirm(`Delete team "${team.name}"? All its members will be removed.`)) return;
    try {
      await onDelete(team);
    } catch {
      // toast handled upstream
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="teams-manager-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Manage teams</DialogTitle>
          <DialogDescription>Rename, delete or add teams in your workspace.</DialogDescription>
        </DialogHeader>

        <div className="max-h-[360px] overflow-y-auto -mx-2 px-2 space-y-1.5">
          {teams.length === 0 && (
            <p className="py-6 text-center text-sm text-slate-400">No teams yet — add one below.</p>
          )}
          {teams.map((team) => {
            const tone = teamTonesById[team.id] || { bg: "#E2E8F0", text: "#334155" };
            const count = (membersByTeam[team.id] || []).length;
            return (
              <div
                key={team.id}
                className="group flex items-center gap-3 h-12 px-3 rounded-xl border border-slate-200 hover:border-slate-300 bg-white transition-colors"
                data-testid={`manage-team-${team.id}`}
              >
                <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: tone.text }} />
                <input
                  defaultValue={team.name}
                  onChange={(e) => setDrafts((d) => ({ ...d, [team.id]: e.target.value }))}
                  onBlur={() => rename(team)}
                  onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                  data-testid={`manage-team-name-${team.id}`}
                  className="flex-1 min-w-0 bg-transparent text-sm font-medium text-slate-800 outline-none focus:bg-slate-50 focus:px-2 focus:rounded-md h-8"
                />
                <span className="text-[11px] font-medium text-slate-500 whitespace-nowrap">
                  {count} {count === 1 ? "member" : "members"}
                </span>
                <button
                  onClick={() => removeTeam(team)}
                  data-testid={`manage-team-delete-${team.id}`}
                  className="opacity-0 group-hover:opacity-100 h-8 w-8 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition-all flex items-center justify-center"
                  title="Delete team"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-2 pt-3 border-t border-slate-100">
          <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Add team</Label>
          <div className="mt-2 flex items-center gap-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addTeam(); }}
              placeholder="Team name"
              data-testid="manage-team-new-name"
              className="flex-1"
            />
            <Button onClick={addTeam} disabled={adding || !newName.trim()} data-testid="manage-team-add" className="shrink-0">
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Users() {
  const [teams, setTeams] = useState([]);
  const [members, setMembers] = useState([]);
  const [agentNames, setAgentNames] = useState([]);
  const [newMemberOpen, setNewMemberOpen] = useState(false);
  const [manageTeamsOpen, setManageTeamsOpen] = useState(false);
  const [permsOpen, setPermsOpen] = useState(false);
  const [permsMember, setPermsMember] = useState(null);
  const [hoursOpen, setHoursOpen] = useState(false);
  const [hoursMember, setHoursMember] = useState(null);
  const [hoursData, setHoursData] = useState(null);
  const [hoursLoading, setHoursLoading] = useState(false);
  const [targetTeamId, setTargetTeamId] = useState(null);
  const [expandedTeams, setExpandedTeams] = useState(new Set());

  const toggleTeam = (id) => {
    setExpandedTeams((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const load = async () => {
    try {
      const [t, m, a] = await Promise.all([api.get("/teams"), api.get("/members"), api.get("/agents")]);
      setTeams(t.data);
      setMembers(m.data);
      const names = Array.from(new Set((a.data || []).map((x) => x.agent_name).filter(Boolean))).sort();
      setAgentNames(names);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { load(); }, []);

  const membersByTeam = useMemo(() => {
    const map = {};
    for (const t of teams) map[t.id] = [];
    for (const m of members) {
      if (!map[m.team_id]) map[m.team_id] = [];
      map[m.team_id].push(m);
    }
    return map;
  }, [teams, members]);

  const teamTonesById = useMemo(() => {
    const map = {};
    teams.forEach((t, idx) => { map[t.id] = teamTone(idx); });
    return map;
  }, [teams]);

  const handleCreateMember = async (payload) => {
    const { data } = await api.post("/members", payload);
    setMembers((prev) => [...prev, data]);
  };

  const handleUpdateMember = async (id, patch) => {
    try {
      const { data } = await api.patch(`/members/${id}`, patch);
      setMembers((prev) => prev.map((m) => (m.id === id ? data : m)));
    } catch {
      toast.error("Update failed");
    }
  };

  const handleDeleteMember = async (id) => {
    try {
      await api.delete(`/members/${id}`);
      setMembers((prev) => prev.filter((m) => m.id !== id));
      toast.success("Member removed");
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleDuplicateMember = async (m) => {
    try {
      const baseEmail = m.email || "";
      const [local, domain] = baseEmail.includes("@") ? baseEmail.split("@") : [baseEmail, ""];
      const newEmail = domain ? `${local}+copy-${Date.now().toString(36).slice(-4)}@${domain}` : `${baseEmail}-copy`;
      const payload = {
        name: `${m.name} (copy)`,
        email: newEmail,
        team_id: m.team_id,
        designation: m.designation || "",
        permissions: m.permissions || ["dashboard"],
      };
      const { data } = await api.post("/members", payload);
      setMembers((prev) => [...prev, data]);
      toast.success("Member duplicated");
    } catch {
      toast.error("Duplicate failed");
    }
  };

  const handleCreateTeam = async (payload) => {
    const { data } = await api.post("/teams", payload);
    setTeams((prev) => [...prev, data]);
  };

  const handleUpdateTeam = async (id, patch) => {
    const { data } = await api.patch(`/teams/${id}`, patch);
    setTeams((prev) => prev.map((t) => (t.id === id ? data : t)));
  };

  const handleDeleteTeam = async (team) => {
    try {
      await api.delete(`/teams/${team.id}`);
      setTeams((prev) => prev.filter((t) => t.id !== team.id));
      setMembers((prev) => prev.filter((m) => m.team_id !== team.id));
      toast.success("Team deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  // Variant used by inline group header (already confirms)
  const handleDeleteTeamWithConfirm = async (team) => {
    if (!window.confirm(`Delete team "${team.name}"? All its members will be removed.`)) return;
    await handleDeleteTeam(team);
  };

  const openNewMember = (teamId) => {
    setTargetTeamId(teamId || null);
    setNewMemberOpen(true);
  };

  const openPermissions = (member) => {
    setPermsMember(member);
    setPermsOpen(true);
  };

  const openHours = async (member) => {
    setHoursMember(member);
    setHoursOpen(true);
    setHoursData(null);
    setHoursLoading(true);
    try {
      const { data } = await api.get(`/members/${member.id}/timeline-summary`);
      setHoursData(data);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load hours");
    } finally {
      setHoursLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900">Directory</h2>
          <p className="text-sm text-slate-500">Organized by team · click any cell to edit inline</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setManageTeamsOpen(true)} data-testid="manage-teams-button" className="h-10 rounded-xl">
            <Plus className="h-4 w-4 mr-1" /> Teams
          </Button>
          <Button onClick={() => openNewMember(null)} data-testid="new-member-button" className="btn-brand h-10 rounded-xl">
            <UserPlus className="h-4 w-4 mr-1" /> Add member
          </Button>
        </div>
      </div>

      {teams.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <p className="text-sm text-slate-500">No teams yet — create one to get started.</p>
          <Button onClick={() => setManageTeamsOpen(true)} className="mt-4">Create team</Button>
        </div>
      ) : (
        <div className="space-y-6">
          {teams.map((team, idx) => {
            const tone = teamTonesById[team.id] || teamTone(idx);
            const items = membersByTeam[team.id] || [];
            return (
              <div key={team.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={`team-${team.id}`}>
                {/* Group header (accordion toggle) */}
                <div
                  className={`h-12 px-4 flex items-center gap-3 border-b border-slate-200 cursor-pointer transition-colors ${
                    expandedTeams.has(team.id) ? "bg-slate-50" : "bg-slate-50 hover:bg-slate-100/70"
                  }`}
                  onClick={() => toggleTeam(team.id)}
                >
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleTeam(team.id); }}
                    data-testid={`team-toggle-${team.id}`}
                    className="h-7 w-7 rounded-md hover:bg-white text-slate-500 hover:text-slate-900 flex items-center justify-center"
                    aria-label={expandedTeams.has(team.id) ? "Collapse" : "Expand"}
                  >
                    {expandedTeams.has(team.id) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: tone.text }} />
                  <span className="font-medium text-slate-800">{team.name}</span>
                  <span className="text-xs text-slate-500">
                    {items.length} {items.length === 1 ? "member" : "members"}
                  </span>
                  <div className="ml-auto flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => openNewMember(team.id)}
                      data-testid={`add-member-${team.id}`}
                      className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900 px-2 h-7 rounded-md hover:bg-white"
                    >
                      <Plus className="h-3.5 w-3.5" /> Add member
                    </button>
                    <button
                      onClick={() => handleDeleteTeamWithConfirm(team)}
                      data-testid={`delete-team-${team.id}`}
                      className="h-7 w-7 rounded-md text-slate-400 hover:text-rose-600 hover:bg-white flex items-center justify-center"
                      title="Delete team"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

              {expandedTeams.has(team.id) && (<>
                {/* Column headers */}
                <div className="grid grid-cols-[60px_1fr_1fr_160px_1fr_140px_88px] text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500 px-4 h-9 items-center border-b border-slate-100">
                  <div></div>
                  <div>Name</div>
                  <div>Email</div>
                  <div>Team</div>
                  <div>Designation</div>
                  <div className="text-center">Permissions</div>
                  <div className="text-center">Actions</div>
                </div>

                {/* Rows */}
                {items.length === 0 ? (
                  <div className="px-4 py-8 text-center text-xs text-slate-400">No members in this team yet</div>
                ) : (
                  items.map((m) => (
                    <div
                      key={m.id}
                      className="grid grid-cols-[60px_1fr_1fr_160px_1fr_140px_88px] items-center gap-2 px-4 h-14 border-b border-slate-100 hover:bg-slate-50/60 transition-colors group"
                      data-testid={`member-row-${m.id}`}
                    >
                      <AvatarPill name={m.name} tone={teamTonesById[m.team_id] || tone} />
                      <input
                        defaultValue={m.name}
                        onBlur={(e) => {
                          if (e.target.value !== m.name && e.target.value.trim()) {
                            handleUpdateMember(m.id, { name: e.target.value });
                          } else {
                            e.target.value = m.name;
                          }
                        }}
                        className="bg-transparent text-sm font-medium text-slate-800 outline-none focus:bg-white focus:px-2 focus:rounded-md focus:border focus:border-slate-200 h-8 transition-all"
                        data-testid={`member-name-${m.id}`}
                      />
                      <input
                        defaultValue={m.email}
                        onBlur={(e) => {
                          if (e.target.value !== m.email && e.target.value.trim()) {
                            handleUpdateMember(m.id, { email: e.target.value });
                          } else {
                            e.target.value = m.email;
                          }
                        }}
                        className="bg-transparent text-sm text-slate-600 outline-none focus:bg-white focus:px-2 focus:rounded-md focus:border focus:border-slate-200 h-8 transition-all"
                        data-testid={`member-email-${m.id}`}
                      />
                      <Select value={m.team_id} onValueChange={(v) => handleUpdateMember(m.id, { team_id: v })}>
                        <SelectTrigger
                          data-testid={`member-team-${m.id}`}
                          className="h-8 border-none rounded-md font-medium text-xs w-full justify-between px-2 hover:brightness-95 focus:ring-0 focus:ring-offset-0"
                          style={{ background: (teamTonesById[m.team_id] || tone).bg, color: (teamTonesById[m.team_id] || tone).text }}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <input
                        defaultValue={m.designation || ""}
                        onBlur={(e) => {
                          if (e.target.value !== (m.designation || "")) {
                            handleUpdateMember(m.id, { designation: e.target.value });
                          }
                        }}
                        placeholder="—"
                        data-testid={`member-designation-${m.id}`}
                        className="bg-transparent text-sm text-slate-700 outline-none focus:bg-white focus:px-2 focus:rounded-md focus:border focus:border-slate-200 h-8 placeholder:text-slate-400 transition-all"
                      />
                      <button
                        onClick={() => openPermissions(m)}
                        data-testid={`member-perms-${m.id}`}
                        className="h-8 rounded-md bg-slate-50 border border-slate-200 hover:border-slate-300 hover:bg-white text-xs font-medium text-slate-700 flex items-center justify-center gap-1.5 transition-colors"
                      >
                        <ShieldCheck className="h-3.5 w-3.5 text-blue-600" />
                        {(m.permissions || []).length}/{SECTIONS.length}
                      </button>
                      <button
                        onClick={() => openHours(m)}
                        data-testid={`member-hours-${m.id}`}
                        className="h-8 w-8 rounded-md bg-slate-50 border border-slate-200 hover:border-emerald-300 hover:bg-mint-50 text-slate-700 flex items-center justify-center transition-colors"
                        title="Hours summary"
                      >
                        <Clock className="h-4 w-4 text-emerald-600" />
                      </button>
                      <div className="flex items-center justify-end gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleDuplicateMember(m)}
                          className="h-8 w-8 rounded-lg hover:bg-sky-50 text-slate-400 hover:text-blue-600 transition-all flex items-center justify-center"
                          data-testid={`member-duplicate-${m.id}`}
                          title="Duplicate"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteMember(m.id)}
                          className="h-8 w-8 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition-all flex items-center justify-center"
                          data-testid={`member-delete-${m.id}`}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </>)}
              </div>
            );
          })}
        </div>
      )}

      <NewMemberDialog
        open={newMemberOpen}
        onOpenChange={setNewMemberOpen}
        teams={teams}
        defaultTeamId={targetTeamId}
        onCreate={handleCreateMember}
      />
      <TeamsManagerDialog
        open={manageTeamsOpen}
        onOpenChange={setManageTeamsOpen}
        teams={teams}
        membersByTeam={membersByTeam}
        teamTonesById={teamTonesById}
        onCreate={handleCreateTeam}
        onUpdate={handleUpdateTeam}
        onDelete={handleDeleteTeam}
      />
      <PermissionsDialog
        open={permsOpen}
        onOpenChange={setPermsOpen}
        member={permsMember}
        agentNames={agentNames}
        onSave={(id, perms, sectionPerms) => handleUpdateMember(id, { permissions: perms, section_permissions: sectionPerms })}
      />
      <HoursDialog
        open={hoursOpen}
        onOpenChange={setHoursOpen}
        member={hoursMember}
        data={hoursData}
        loading={hoursLoading}
      />
    </div>
  );
}

function HoursDialog({ open, onOpenChange, member, data, loading }) {
  if (!member) return null;
  const COLS = "150px 100px 1fr";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="hours-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Clock className="h-5 w-5 text-emerald-600" />
            Hours · {member.name}
          </DialogTitle>
          <DialogDescription>Aggregated from Agent Workflow → Timeline entries assigned to <span className="font-medium text-slate-800">{member.email}</span>.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-sm text-slate-400">Loading…</div>
        ) : !data || data.by_date.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-sm text-slate-500">No timeline entries assigned to this member yet.</p>
            <p className="text-xs text-slate-400 mt-1">Assign tasks via Agent Workflow → ⌚ Timeline icon on any row.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-xl bg-mint-50 border border-mint-100 p-3" data-testid="hours-total">
                <p className="text-[11px] uppercase tracking-[0.12em] text-emerald-700 font-bold">Total hours</p>
                <p className="text-2xl font-display font-semibold text-emerald-900">{data.total_hours}</p>
              </div>
              <div className="rounded-xl bg-sky-50 border border-sky-100 p-3" data-testid="hours-tasks">
                <p className="text-[11px] uppercase tracking-[0.12em] text-sky-700 font-bold">Total tasks</p>
                <p className="text-2xl font-display font-semibold text-sky-900">{data.total_tasks}</p>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <div
                className="grid gap-2 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-700 px-3 h-10 items-center bg-slate-100/70 border-b border-slate-200"
                style={{ gridTemplateColumns: COLS }}
              >
                <div>Date</div>
                <div>Total hrs</div>
                <div>Task details</div>
              </div>
              {data.by_date.map((row) => (
                <div
                  key={row.date || "no-date"}
                  className="grid gap-2 px-3 py-2 border-b border-slate-100 hover:bg-slate-50/40"
                  style={{ gridTemplateColumns: COLS }}
                  data-testid={`hours-row-${row.date}`}
                >
                  <div className="text-sm font-medium text-slate-800">{row.date || <span className="text-slate-400">no date</span>}</div>
                  <div className="text-sm font-mono text-emerald-700 font-semibold">{row.total_hours}</div>
                  <div className="space-y-1">
                    {row.tasks.map((t, i) => (
                      <div key={i} className="text-xs text-slate-600 flex items-center gap-2">
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-500" />
                        <span className="flex-1 truncate">{t.task || <span className="text-slate-400">untitled</span>}</span>
                        <span className="text-emerald-700 font-mono">{t.hours}h</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
